import React, { useState, useEffect } from "react";
import {
  Modal,
  PrimaryBlueButton,
  PrimaryGrayButton,
  Toggle,
  Severity
} from "@velaro/velaro-component-lib";
import { AzureIndex } from "../types";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";

interface Props {
  index?: AzureIndex; // If provided, we're editing; otherwise creating
  onClose: () => void;
  onSave: (index: AzureIndex) => Promise<void>;
  isLoading?: boolean;
}

const INDEX_TYPES = [
  { value: "Vector", label: "Vector (Recommended)" },
  { value: "UserDefined", label: "User Defined" }
];

export default function IndexModal({ index, onClose, onSave, isLoading }: Props) {
  const isEditing = !!index?.id;
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();

  const [formData, setFormData] = useState<AzureIndex>({
    id: index?.id || 0,
    name: index?.name || "",
    siteId: index?.siteId || 0,
    type: index?.type || "Vector",
    searchSize: index?.searchSize || 3,
    semanticSearchEnabled: index?.semanticSearchEnabled || false
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (index) {
      setFormData(index);
    }
  }, [index]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      toastAlert.displayToast(Severity.Error, "Please enter an index name");
      return;
    }

    if (formData.searchSize < 1 || formData.searchSize > 9) {
      toastAlert.displayToast(Severity.Error, "Search size must be between 1 and 9");
      return;
    }

    // Name validation: only lowercase letters, numbers, and hyphens
    const nameRegex = /^[a-z0-9-]+$/;
    if (!isEditing && !nameRegex.test(formData.name.toLowerCase())) {
      toastAlert.displayToast(
        Severity.Error,
        "Index name can only contain lowercase letters, numbers, and hyphens"
      );
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        name: formData.name.toLowerCase()
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="max-w-lg" show={true}>
      <div className="bg-white p-6 rounded-2xl w-full">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Index" : "Create Index"}
          </h2>
          <p className="text-sm text-slate-500">
            {isEditing
              ? "Update the index configuration"
              : "Create a new Azure Search index for AI training data"}
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Index Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Index Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., my-knowledge-base"
              className="w-full p-2 border rounded-md text-sm"
              disabled={isEditing} // Can't change name after creation
            />
            {!isEditing && (
              <p className="text-xs text-slate-400 mt-1">
                Lowercase letters, numbers, and hyphens only
              </p>
            )}
          </div>

          {/* Index Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Index Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full p-2 border rounded-md text-sm"
              disabled={isEditing}
            >
              {INDEX_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search Size */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Search Size (1-9)
            </label>
            <input
              type="number"
              min={1}
              max={9}
              value={formData.searchSize}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  searchSize: Math.min(9, Math.max(1, parseInt(e.target.value) || 1))
                })
              }
              className="w-full p-2 border rounded-md text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">
              Number of search results to return (K for KNN search)
            </p>
          </div>

          {/* Semantic Search Toggle */}
          {subscription?.enableSemanticSearch && (
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium text-slate-700">
                  Enhanced AI Search
                </div>
                <div className="text-xs text-slate-400">
                  Enable semantic search for improved results
                </div>
              </div>
              <Toggle
                toggled={formData.semanticSearchEnabled}
                changeToggle={() =>
                  setFormData({
                    ...formData,
                    semanticSearchEnabled: !formData.semanticSearchEnabled
                  })
                }
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 gap-3">
          <PrimaryGrayButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton
            label={saving ? "Saving..." : isEditing ? "Save" : "Create"}
            onClick={handleSubmit}
            disabled={saving || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}
